import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Input } from '../../components/ui/input';
import { Loader2, RotateCcw, Info, RefreshCw, Landmark } from 'lucide-react';
import { getMerchants } from '../../services/adminApi';
import { manualAepsSettlement, refreshAepsBanks } from '../../services/api';
import { showLoadingToast, updateToast, showErrorToast, showSuccessToast } from '../../utils/toast';

const SearchableMerchantSelect = ({ merchants, value, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');

  // Filter to include only ACTIVE merchants, then match search term
  const activeMerchants = merchants.filter(m => (m.status || 'ACTIVE').toUpperCase() === 'ACTIVE');
  const filtered = activeMerchants.filter(m => 
    (m.merchantName || m.name || '').toLowerCase().includes(search.toLowerCase()) ||
    (m.merchantId || m.id || '').toLowerCase().includes(search.toLowerCase())
  );

  const selectedMerchant = activeMerchants.find(m => (m.merchantId || m.id) === value);
  const displayValue = value === 'all' 
    ? 'All Merchants (Bulk Settlement)' 
    : selectedMerchant 
      ? `${selectedMerchant.merchantName || selectedMerchant.name} (${selectedMerchant.merchantId || selectedMerchant.id})`
      : 'Select merchant (Optional)';

  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (!e.target.closest('.searchable-dropdown-container')) {
        setIsOpen(false);
      }
    };
    document.addEventListener('click', handleOutsideClick);
    return () => document.removeEventListener('click', handleOutsideClick);
  }, []);

  return (
    <div className="relative searchable-dropdown-container w-full">
      <div 
        onClick={() => setIsOpen(!isOpen)}
        className="flex h-10 w-full items-center justify-between rounded-md border border-input bg-white px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 cursor-pointer"
      >
        <span className="truncate">{displayValue}</span>
        <span className="text-gray-400 text-xs">▼</span>
      </div>

      {isOpen && (
        <div className="absolute z-50 min-w-[8rem] w-full overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-md animate-in fade-in-80 mt-1 bg-white">
          <div className="p-2 border-b border-gray-100">
            <input
              type="text"
              placeholder="Search active merchant..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              onClick={(e) => e.stopPropagation()}
              autoFocus
            />
          </div>
          <div className="max-h-[220px] overflow-y-auto p-1">
            <div
              onClick={() => {
                onChange('all');
                setIsOpen(false);
                setSearch('');
              }}
              className="relative flex w-full cursor-pointer select-none items-center rounded-sm py-1.5 px-2 text-sm outline-none hover:bg-slate-100 hover:text-accent-foreground"
            >
              All Merchants (Bulk Settlement)
            </div>
            {filtered.length === 0 ? (
              <div className="py-2 px-2 text-xs text-gray-500 text-center">No active merchants found</div>
            ) : (
              filtered.map(m => (
                <div
                  key={m.id || m.merchantId}
                  onClick={() => {
                    onChange(m.merchantId || m.id);
                    setIsOpen(false);
                    setSearch('');
                  }}
                  className="relative flex w-full cursor-pointer select-none items-center rounded-sm py-1.5 px-2 text-sm outline-none hover:bg-slate-100 hover:text-accent-foreground"
                >
                  {m.merchantName || m.name} ({m.merchantId || m.id})
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const AepsSettlement = () => {
  const [merchants, setMerchants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  
  const [formData, setFormData] = useState({
    merchantId: 'all',
    cutoffDateTime: ''
  });

  useEffect(() => {
    loadMerchants();
  }, []);

  const loadMerchants = async () => {
    try {
      setLoading(true);
      const response = await getMerchants(1, 1000, 'all');
      
      let merchantList = [];
      if (response.data) {
        if (Array.isArray(response.data)) {
          merchantList = response.data;
        } else if (response.data.content && Array.isArray(response.data.content)) {
          merchantList = response.data.content;
        }
      }
      setMerchants(merchantList);
    } catch (error) {
      console.error('Failed to load merchants:', error);
      showErrorToast('Failed to load merchants');
    } finally {
      setLoading(false);
    }
  };

  const handleSettlementSubmit = async (e) => {
    e.preventDefault();
    
    setSubmitting(true);
    const toastId = showLoadingToast('Triggering AEPS settlement process...');

    try {
      const params = {};
      if (formData.merchantId && formData.merchantId !== 'all') {
        params.merchantId = formData.merchantId;
      }
      
      if (formData.cutoffDateTime) {
        const dateStr = formData.cutoffDateTime.length === 16 
          ? `${formData.cutoffDateTime}:00` 
          : formData.cutoffDateTime;
        params.cutoffDateTime = dateStr;
      }

      await manualAepsSettlement(params);
      
      updateToast(toastId, 'Manual AEPS settlement triggered successfully!', 'success');
      
      setFormData({
        merchantId: 'all',
        cutoffDateTime: ''
      });
    } catch (error) {
      console.error('Failed to trigger AEPS settlement:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to trigger AEPS settlement';
      updateToast(toastId, errorMessage, 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSyncBanks = async () => {
    setSyncing(true);
    const toastId = showLoadingToast('Syncing AEPS bank list...');
    try {
      await refreshAepsBanks();
      updateToast(toastId, 'AEPS bank list cached successfully!', 'success');
    } catch (error) {
      console.error('Failed to sync AEPS banks:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to force-refresh AEPS bank list';
      updateToast(toastId, errorMessage, 'error');
    } finally {
      setSyncing(false);
    }
  };

  if (loading && merchants.length === 0) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="w-full space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">AEPS Operations & Settlements</h1>
          <p className="text-gray-500 mt-1">Manage manual AEPS settlements and synchronize bank configurations</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Manual Settlement Configuration Form */}
        <Card className="lg:col-span-2 shadow-sm border-gray-200">
          <CardHeader className="pb-4 border-b bg-white">
            <CardTitle className="text-lg font-bold flex items-center gap-2">
              <RotateCcw className="h-5 w-5 text-blue-600" />
              Manual AEPS Settlement
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <form onSubmit={handleSettlementSubmit} className="space-y-6">
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label className="text-gray-700 font-semibold">Target Merchant</Label>
                <SearchableMerchantSelect 
                  merchants={merchants}
                  value={formData.merchantId} 
                  onChange={(val) => setFormData({...formData, merchantId: val})}
                />
                  <p className="text-xs text-gray-500">Select a merchant or settle for all merchants collectively.</p>
                </div>

                <div className="space-y-2">
                  <Label className="text-gray-700 font-semibold">Cutoff Date & Time (Optional)</Label>
                  <Input 
                    type="datetime-local" 
                    value={formData.cutoffDateTime} 
                    onChange={(e) => setFormData({...formData, cutoffDateTime: e.target.value})} 
                    className="bg-white"
                  />
                  <p className="text-xs text-gray-500">Settle successful cash-withdrawals created BEFORE this time.</p>
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 flex gap-3">
                <Info className="h-5 w-5 text-blue-600 shrink-0 mt-0.5" />
                <div className="text-sm text-blue-800">
                  <p className="font-semibold">Important Note:</p>
                  <p className="mt-1">
                    Settles successful cash-withdrawal transactions (Settlement-Status=PENDING). 
                    The settled amount will be added to the merchant's usable wallet balance.
                  </p>
                </div>
              </div>

              <Button 
                type="submit" 
                className="w-full bg-blue-600 hover:bg-blue-700 text-white h-11 text-base font-semibold transition-colors shadow-sm"
                disabled={submitting}
              >
                {submitting ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    Processing AEPS Settlement...
                  </>
                ) : (
                  'Run AEPS Settlement Now'
                )}
              </Button>

            </form>
          </CardContent>
        </Card>

        {/* Sync AEPS Bank list card */}
        <Card className="shadow-sm border-gray-200 h-fit">
          <CardHeader className="pb-4 border-b bg-white">
            <CardTitle className="text-lg font-bold flex items-center gap-2">
              <Landmark className="h-5 w-5 text-indigo-600" />
              AEPS Bank List
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6 space-y-4">
            <Typography variant="body2" className="text-sm text-gray-600 leading-relaxed">
              Synchronize the local AEPS support banks cache directly with the gateway's active endpoints. 
              Run this refresh if merchants report out-of-sync or missing bank options during transactions.
            </Typography>

            <div className="bg-indigo-50 border border-indigo-100 rounded-lg p-3.5 flex gap-3">
              <Info className="h-4 w-4 text-indigo-600 shrink-0 mt-0.5" />
              <div className="text-xs text-indigo-800 leading-relaxed">
                Re-syncing fetches the latest active banks list including IIN routing details, routing servers, and status indicators.
              </div>
            </div>

            <Button 
              onClick={handleSyncBanks} 
              className="w-full bg-indigo-600 hover:bg-indigo-700 text-white h-10 text-sm font-semibold transition-colors shadow-sm flex items-center justify-center gap-2"
              disabled={syncing}
            >
              {syncing ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Refreshing Bank Cache...
                </>
              ) : (
                <>
                  <RefreshCw className="h-4 w-4" />
                  Sync Bank List Now
                </>
              )}
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Simple Typography wrapper component to keep JSX clean without importing more UI frameworks
const Typography = ({ variant, className, children }) => {
  if (variant === 'body2') {
    return <p className={`text-sm text-gray-600 ${className || ''}`}>{children}</p>;
  }
  return <span className={className}>{children}</span>;
};

export default AepsSettlement;
